import React, { useContext, useEffect, useRef, useState } from 'react';
import { Route, Routes, useParams } from 'react-router-dom';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import { Context as RegistrationAdminDashboardContext } from '../../../providers/RegistrationAdminDashboardProvider';
import { triggerNotification } from '../../../helpers/notificationHelper';
import StoreOrderDetailsView from './StoreOrderDetailsView';
import RegCartOrderDetailsView from './RegCartOrderDetailsView';
import { useModalState } from '../../../helpers/hooks';
import ExportCsvModal from '../bulkEdit/ExportCsvModal';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import { ecomOrderExportConfig } from './ecomOrderExportConfig';
import { regCartOrderExportConfig } from './regCartOrderExportConfig';
import SendOrderReceiptModal from '../financials/SendOrderReceiptModal';
import { REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM } from '../../../config/constants';

const ORDER_VIEWS = {
  COMPLETE: 1,
  FORM_SUBMISSION: 2,
  ECOM_ORDER: 3
};

const RegCartOrderViews = () => {
  const hasFetched = useRef(false);
  const { pkEcomOrder, pkRegCart } = useParams();
  const { state: helperState } = useContext(RegistrationAdminDashboardContext);
  const { state, fetchRegCartOrder, fetchPaginatedRegCartOrders } = useContext(
    RegistrationAdminContext
  );
  const { state: modalState, onOpenModal, onCloseModal } = useModalState();
  const [viewState, setViewState] = useState({ view: ORDER_VIEWS.COMPLETE });
  const exportShopOrder = modalState.action === 'export_shop_order';

  const regCart =
    state.regCart.value &&
    (state.regCart.value.pkRegCart.toString() === pkRegCart ||
      (pkEcomOrder &&
        state.regCart.value.ecomOrder?.pkEcomOrder.toString() === pkEcomOrder))
      ? state.regCart.value
      : null;

  const loading = (!regCart && !hasFetched.current) || state.regCart.loading;

  useEffect(() => {
    if (!regCart && (pkEcomOrder || pkRegCart)) {
      fetchRegCartOrder(
        {
          pkEcomOrder,
          pkRegCart
        },
        null,
        (e) => {
          triggerNotification(e);
        }
      );

      setViewState({
        ...viewState,
        view: ORDER_VIEWS.COMPLETE
      });

      hasFetched.current = true;
    }
  }, [pkEcomOrder, pkRegCart]);

  return (
    <>
      <Routes>
        <Route element={<></>} path="/refund" />
        <Route
          element={
            pkEcomOrder ? (
              <StoreOrderDetailsView
                baseUrl="/admin/financials/orders"
                ecomOrder={regCart?.ecomOrder}
                loading={loading}
                onAction={(action) => onOpenModal(action)}
                pkRegCart={regCart?.pkRegCart}
              />
            ) : (
              <RegCartOrderDetailsView
                baseUrl="/admin/financials/orders"
                loading={loading}
                onAction={(action) => onOpenModal(action)}
                regCart={regCart}
              />
            )
          }
          path="*"
        />
      </Routes>

      <SendOrderReceiptModal
        isAdminViewing
        isOpen={
          modalState.isOpen &&
          modalState.action ===
            REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.resendReceipt
        }
        onClose={onCloseModal}
        regCart={regCart}
      />

      <ExportCsvModal
        confirmMessage={
          <AppStack style={{ gap: 8 }}>
            <AppText style={{ fontSize: 14 }}>
              Exporting this order will include all the details of the order in
              a CSV file.
            </AppText>

            <AppText style={{ fontSize: 14, fontWeight: '500' }}>
              Are you sure you want to proceed?
            </AppText>
          </AppStack>
        }
        descriptor="order"
        exportConfig={
          exportShopOrder ? ecomOrderExportConfig : regCartOrderExportConfig
        }
        isOpen={
          modalState.isOpen &&
          (modalState.action === 'export_order' || exportShopOrder)
        }
        itemType="order"
        itemTypePlural="orders"
        onClose={onCloseModal}
        onExport={({ items }, downloadCsvCallback, onErrorCallback) => {
          fetchPaginatedRegCartOrders(
            regCart.regAssociation.pkRegAssociation ||
              helperState.regAssociation.value?.pkRegAssociation,
            {
              pkRegCarts: [regCart.pkRegCart],
              skipStateUpdate: true
            },
            (result) => {
              const csvData = exportShopOrder
                ? ecomOrderExportConfig.getCsvData(
                    result.data
                      .filter((e) => e.ecomOrder)
                      .map((r) => r.ecomOrder)
                  )
                : regCartOrderExportConfig.getCsvData(result.data);
              downloadCsvCallback(csvData);
            },
            onErrorCallback
          );
        }}
        selectedItems={exportShopOrder ? [regCart.ecomOrder] : [regCart]}
      />
    </>
  );
};

RegCartOrderViews.propTypes = {};

export default RegCartOrderViews;
